/**
 * Виджет «Освоение по заказчикам (АБП)» (US-26 FR-6).
 *
 * Таблица «заказчик · общая стоимость · всего освоено · освоение (полоса
 * и процент) · проектов», отсортированная по общей стоимости по убыванию;
 * итоговой строки нет (FR-6.3). Охват — общий охват раздела, подписан в
 * заголовке виджета (FR-6.2).
 *
 * Наименование заказчика выводится в формулировке справочника бюджетной
 * классификации; экран его не переименовывает, при нехватке ширины
 * обрезает с полным значением в подсказке (FR-6.4).
 *
 * «Освоение» здесь — отношение **всего освоенного к общей стоимости**, а
 * не процент к годовому плану из плашки (FR-5.3): две разные величины
 * одним словом не подписываются.
 */
import { useTranslation } from 'react-i18next';

import { fmt } from './format';
import { DevelopmentBar, MoneyMln, NoData, useLocalizedText } from './projectsView';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import type { ProjectsAbpRow } from '../../../types/projects';

interface AbpWidgetProps {
  rows: ProjectsAbpRow[];
}

export const AbpWidget = ({ rows }: AbpWidgetProps) => {
  const { t } = useTranslation();
  const loc = useLocalizedText();

  // FR-6.3: сортировка по общей стоимости по убыванию. Выдача отдаёт её
  // отсортированной, но порядок на экране от порядка ответа не зависит.
  const sorted = [...rows].sort(
    (a, b) => (b.totalCost ?? 0) - (a.totalCost ?? 0),
  );

  return (
    <Card className="h-full shadow-card" data-testid="block-projects-abp">
      <CardHeader className="flex-row flex-wrap items-baseline gap-x-3 gap-y-1 space-y-0">
        <CardTitle>{t('projects.abp.title')}</CardTitle>
        <span className="text-xs text-text-faint">
          {t('projects.abp.scope')}
        </span>
      </CardHeader>
      <CardContent>
        {sorted.length === 0 ? (
          <p
            className="py-8 text-center text-sm lowercase text-text-muted"
            data-testid="block-placeholder"
          >
            {t('common.noData')}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <Table className="min-w-[38rem]">
              <TableHeader>
                <TableRow>
                  <TableHead>{t('projects.abp.customer')}</TableHead>
                  <TableHead className="whitespace-nowrap text-right">
                    {t('projects.abp.totalCost')}
                  </TableHead>
                  <TableHead className="whitespace-nowrap text-right">
                    {t('projects.abp.developedTotal')}
                  </TableHead>
                  <TableHead className="whitespace-nowrap text-right">
                    {t('projects.abp.development')}
                  </TableHead>
                  <TableHead className="whitespace-nowrap text-right">
                    {t('projects.abp.projects')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sorted.map((row) => {
                  const name = loc(row.abpName) ?? row.abpCode;
                  return (
                    <TableRow
                      key={row.abpCode ?? name ?? ''}
                      data-testid={`projects-abp-row-${row.abpCode ?? name}`}
                    >
                      <TableCell
                        className="max-w-72 text-text-muted"
                        title={name ?? undefined}
                      >
                        <span className="line-clamp-2">
                          {name ?? <NoData />}
                        </span>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        <MoneyMln value={row.totalCost} />
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        <MoneyMln value={row.spentTotal} />
                      </TableCell>
                      <TableCell>
                        <DevelopmentBar pct={row.executionPct} />
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {fmt(row.projectCount)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
